import type { AgentRunRow } from '../../lib/database.types'
import { elapsed } from '../../lib/time'
import { usd, tokens } from '../../lib/money'
import { faceFor } from '../../lib/glyph'
import { stageFor, isLive, type Stage } from './stage'

/**
 * A lane is a slot, not a run: (department, lane_type) is the slot, and the lane
 * shows whatever is newest in it. That is what makes the spec's idle lane
 * ("Lane reserved. No agent assigned yet") coherent -- a slot can exist with
 * nothing in it -- and it means a finished run stays visible in its lane until
 * the next one starts, which is what an operator glancing at the board wants.
 */
export interface Lane {
  key: string
  department: string
  laneType: string
  agent: string | null
  face: string
  task: string
  runId: string
  externalRef: string | null
  externalUrl: string | null
  prUrl: string | null
  branch: string | null
  stage: Stage
  elapsed: string
  live: boolean
  cost: string
  outputTokens: string
  /** Cache reads dwarf raw input and are priced differently; kept separate. */
  cacheRead: string
  attempt: number
}

export function toLanes(runs: ReadonlyArray<AgentRunRow>, now = Date.now()): Lane[] {
  const newestPerSlot = new Map<string, AgentRunRow>()
  for (const run of runs) {
    const key = `${run.department}/${run.lane_type}`
    const held = newestPerSlot.get(key)
    // A live run always wins its slot over a finished one, however recent the
    // finished one is -- otherwise a completed run would hide work in flight.
    if (!held) { newestPerSlot.set(key, run); continue }
    const heldLive = isLive(held.status)
    const runLive = isLive(run.status)
    if (runLive && !heldLive) { newestPerSlot.set(key, run); continue }
    if (runLive === heldLive && Date.parse(run.created_at) > Date.parse(held.created_at)) {
      newestPerSlot.set(key, run)
    }
  }

  return [...newestPerSlot.values()]
    .map((run) => ({
      key: `${run.department}/${run.lane_type}`,
      department: run.department,
      laneType: run.lane_type,
      agent: run.agent,
      face: faceFor(run.agent),
      task: run.title ?? run.external_ref ?? 'Untitled run',
      runId: run.id,
      externalRef: run.external_ref,
      externalUrl: run.external_url,
      prUrl: run.pr_url,
      branch: run.branch_name,
      stage: stageFor(run.status, run.attempt),
      elapsed: elapsed(run.started_at ?? run.created_at, run.ended_at, now),
      live: isLive(run.status),
      cost: usd(run.cost_usd),
      outputTokens: tokens(run.output_tokens),
      cacheRead: tokens(run.cache_read_input_tokens),
      attempt: run.attempt,
    }))
    .sort((a, b) => a.department.localeCompare(b.department) || a.laneType.localeCompare(b.laneType))
}

/** Median, not mean: one expensive run should not move the headline number. */
export function medianCost(runs: ReadonlyArray<AgentRunRow>): string {
  const costs = runs
    .map((r) => (r.cost_usd == null ? NaN : Number(r.cost_usd)))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b)
  if (costs.length === 0) return '—'
  const mid = Math.floor(costs.length / 2)
  const value = costs.length % 2 ? costs[mid]! : ((costs[mid - 1]! + costs[mid]!) / 2)
  return usd(value)
}
